import React from 'react'
import { observer } from 'mobx-react'
import styles from './JobProgressionDocumentCreationModal.sass'
import {
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheet,
  ModalSheetHeaderCloseButton,
  ModalSheetHeaderShell,
  ModalSheetContent,
} from 'components/ModalSheet'
import Button from 'components/Button'
import { useDropzone } from 'hooks/use-dropzone'
import FileButton from 'components/FileButton'
import { DocumentCreationItem } from 'documents/views/DocumentCreationModal/DocumentCreationItem'
import JobProgressionViewModel from '../JobProgressionViewModel'
import { match } from 'utils/pattern-match-util'
import { JobProgressionFlow } from 'jobs/progression/JobProgression'

interface Props {
  vm: JobProgressionViewModel
}

function getTitle(progression: JobProgressionFlow) {
  switch (progression) {
    case JobProgressionFlow.PREPARATION:
      return 'Upload preparation notes'
    case JobProgressionFlow.REVIEW:
      return 'Upload review notes'
    default:
      return 'Upload final documents'
  }
}

export default observer(function JobProgressionDocumentCreationModal({
  vm,
}: Props) {
  const documentCreationVm = vm.jobProgressionDocumentCreationVM!

  function onDrop(files: FileList) {
    documentCreationVm.addFiles(files)
  }

  function onEnter() {
    documentCreationVm.setDragging()
  }

  function onLeave() {
    documentCreationVm.stopDragging()
  }

  const [, dropZoneRef] = useDropzone<HTMLDivElement>(onDrop, onEnter, onLeave)

  return (
    <ModalSheet
      ref={dropZoneRef}
      showing={documentCreationVm.showing}
      disableCloseOnOutsideClick={true}
      onRequestClose={() => vm.closeDocumentCreationModal()}
    >
      <ModalSheetHeaderShell size="auto" bordered>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerInfo}>
              <div className={styles.headerTitle}>
                <span>{getTitle(vm.currentProgressionFlow)}</span>
              </div>
              <div className={styles.headerDescription}>
                <span>
                  {match(vm.currentProgressionFlow, {
                    [JobProgressionFlow.RETAIL]: () => (
                      <>
                        Upload the final return and the e-file confirmation to
                        finalize the job
                      </>
                    ),
                    [JobProgressionFlow.OUTSOURCING]: () => (
                      <>Upload the final return and the e-file confirmation</>
                    ),
                    [JobProgressionFlow.ESSENTIAL]: () => (
                      <>
                        Upload the annotated source documents to complete the
                        job.
                        <br />
                        Upload a missing items list if applicable.
                      </>
                    ),
                    [JobProgressionFlow.NONE]: () => (
                      <>Upload the final return and the e-file confirmation</>
                    ),
                    [JobProgressionFlow.PREPARATION]: () => (
                      <>Upload the final documents to complete the job.</>
                    ),
                    [JobProgressionFlow.REVIEW]: () => (
                      <>Upload the final documents to complete the job.</>
                    ),
                    [JobProgressionFlow.PERSONAL_EXTENSION]: () => <>.</>,
                    [JobProgressionFlow.BUSINESS_EXTENSION]: () => <></>,
                    [JobProgressionFlow.OUTSOURCING_EXTENSION]: () => <></>,
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.closeBtn}>
          <ModalSheetHeaderCloseButton onClick={documentCreationVm.close} />
        </div>
      </ModalSheetHeaderShell>
      <ModalSheetContent>
        <div className={styles.creationItemList}>
          {documentCreationVm.documentCreationItemVMs.map(
            (docCreationItemVM, index) => (
              <div key={index}>
                <DocumentCreationItem
                  index={index}
                  vm={docCreationItemVM}
                  removeFile={documentCreationVm.removeFile}
                />
              </div>
            ),
          )}
          <div
            className={
              documentCreationVm.dragging
                ? styles.dropZoneItemDragging
                : styles.dropZoneItem
            }
          >
            <span className={styles.dropZoneTitle}>
              Drag and drop files here or&nbsp;
              <FileButton
                multiple
                onChange={documentCreationVm.addFiles}
                data-test="upload-final-documents-file-input"
              >
                <Button
                  className={styles.dropZoneButton}
                  borderless
                  type="text"
                >
                  Upload another file
                </Button>
              </FileButton>
            </span>
          </div>
        </div>
      </ModalSheetContent>
      <ModalSheetFooter>
        <div className={styles.footer}>
          <div className={styles.uploadButton}>
            <ModalSheetFooterButtonGroup>
              <Button
                type="secondary"
                disabled={vm.isSkipUploadDisabled}
                onClick={() => vm.answerReturnFiled(true)}
              >
                Skip
              </Button>
              <Button
                type="primary"
                dataTest="upload-final-documents-confirm-button"
                disabled={vm.isUploadDisabled}
                onClick={() => vm.answerReturnFiled(false)}
              >
                Upload Documents
              </Button>
            </ModalSheetFooterButtonGroup>
          </div>
        </div>
      </ModalSheetFooter>
    </ModalSheet>
  )
})
